<template>
  <FullScreenModal :open="open" content-class="max-w-screen-2xl" @close="$emit('close')">
    <div>
      <h1 class="text-2xl font-bold">{{ t("title") }}</h1>
    </div>

    <Pills
      class="mt-6 border-b border-gray-200 pb-4"
      :model-value="pillLabels[currentIndex]"
      :options="pillLabels"
      :tones="pillTones"
      :disabled="pillDisabled"
      @update:model-value="currentIndex = pillLabels.indexOf($event)"
    />

    <div class="mt-6">
      <!-- L'avis de l'agent reste monté : un motif en cours de saisie survit à un aller-retour
           vers une pièce du dossier. Réinitialisé à chaque ouverture. Sans décision à prendre,
           il s'affiche en lecture seule : sans sinistre, l'étape ne propose pas de trancher. -->
      <AiAnalysisGeneralStep
        v-if="hasGeneralStep"
        v-show="currentStep.type === 'general_analysis'"
        :key="`general-${openCount}`"
        :analysis="declaration.generalAnalysis"
        :analysable-id="declaration.id"
        :can-retry="hasDecisionSteps"
        :claim="hasDecisionSteps ? claim : null"
        :decided-decision="decidedDecision"
        @decided="onDecided"
        @continue="goToStep('handling_type')"
        @close="$emit('close')"
      />

      <template v-if="currentStep.type !== 'general_analysis'">
        <AiAnalysisContextStep v-if="currentStep.type === 'context'" :claim="claim" :declaration="declaration" />
        <AiAnalysisAlertsStep v-else-if="currentStep.type === 'alerts'" :claim="claim" :customer="customer" />
        <div v-else-if="currentStep.type === 'handling_type'" class="max-w-3xl" data-test="handling-type-step">
          <p class="mb-4 flex items-center gap-2 text-sm font-semibold text-green-700">
            <CheckCircleIcon class="h-5 w-5" aria-hidden="true" />
            {{ t("approved_banner") }}
          </p>
          <HandlingTypeForm :claim="claim" @submitted="$emit('close')" />
        </div>
        <AiAnalysisDocumentStep
          v-else
          :key="currentStep.key"
          :analysis-type="currentStep.analysisType"
          :analysis="currentDocument.analysis"
          :analyzed-at="currentDocument.analyzedAt"
          :verdict="currentDocument.verdict"
          :documents="currentDocument.documents"
          :checks="currentDocument.checks"
          :summary="currentDocument.summary"
          :detail-sections="currentDocument.detailSections"
          :analysable-id="declaration.id"
        />
      </template>
    </div>

    <div class="sticky bottom-0 z-10 mt-8 flex items-center justify-between border-t border-gray-200 bg-white py-4">
      <SecondaryButton :disabled="isFirstStep" @click="previous">{{ t("previous") }}</SecondaryButton>
      <span class="text-xs text-gray-400">{{ currentIndex + 1 }} / {{ steps.length }}</span>
      <SecondaryButton v-if="canGoNext" @click="next">{{ t("next") }}</SecondaryButton>
      <span v-else class="w-20"></span>
    </div>
  </FullScreenModal>
</template>

<script>
import { useI18n } from "vue-i18n";
import { mapGetters } from "vuex";
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { FullScreenModal, Pills, SecondaryButton } from "@estaly/ui";
import AiAnalysisContextStep from "@/components/claim/details/ai-analysis-flow/AiAnalysisContextStep.vue";
import AiAnalysisAlertsStep from "@/components/claim/details/ai-analysis-flow/AiAnalysisAlertsStep.vue";
import AiAnalysisDocumentStep from "@/components/claim/details/ai-analysis-flow/AiAnalysisDocumentStep.vue";
import AiAnalysisGeneralStep from "@/components/claim/details/ai-analysis-flow/AiAnalysisGeneralStep.vue";
import HandlingTypeForm from "@/components/claim/details/action/HandlingTypeForm.vue";
import {
  alertsCount,
  buildAnalysisSteps,
  documentSummary,
  generalAnalysisTone,
  stepVerdict,
  toneFor,
} from "@/components/claim/details/ai-analysis-flow/claimAiAnalysisSummary";

export default {
  name: "AiAnalysisFlowModal",

  components: {
    CheckCircleIcon,
    FullScreenModal,
    Pills,
    SecondaryButton,
    AiAnalysisContextStep,
    AiAnalysisAlertsStep,
    AiAnalysisDocumentStep,
    AiAnalysisGeneralStep,
    HandlingTypeForm,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    open: {
      type: Boolean,
      required: true,
    },
    claim: {
      type: Object,
      required: true,
    },
    declaration: {
      type: Object,
      required: true,
    },
    customer: {
      type: Object,
      default: null,
    },
  },

  emits: ["close"],

  data() {
    return {
      currentIndex: 0,
      // Figé à l'ouverture : une décision rechargée fait sortir le sinistre de l'attente de
      // décision, ce qui retirerait les étapes en plein parcours (avant la prise en charge).
      hasDecisionSteps: false,
      decidedDecision: null,
      openCount: 0,
    };
  },

  computed: {
    ...mapGetters("claims", ["claimApprovalRequired"]),

    steps() {
      return buildAnalysisSteps(this.declaration, { withDecision: this.hasDecisionSteps });
    },
    hasGeneralStep() {
      return this.steps.some((step) => step.type === "general_analysis");
    },
    pillLabels() {
      return this.steps.map((step, index) => {
        const label = `${index + 1}. ${this.t(`step.${step.key}`)}`;
        return step.type === "general_analysis" && this.decidedDecision ? `${label} ✓` : label;
      });
    },
    // Couleur de chaque onglet : le verdict pour les étapes document et l'avis de l'agent,
    // la présence d'alertes pour l'étape alertes. Contexte et prise en charge ne portent
    // aucune analyse, donc gardent l'habillage neutre.
    pillTones() {
      return this.steps.map((step) => {
        if (step.type === "document") return toneFor(stepVerdict(this.declaration, step));
        if (step.type === "general_analysis") return generalAnalysisTone(this.declaration);
        if (step.type === "alerts") return this.hasAlerts ? "yellow" : "green";
        return null;
      });
    },
    // La prise en charge est visible pour montrer le parcours, mais ne s'ouvre qu'une fois approuvé.
    pillDisabled() {
      return this.steps.map((step) => step.type === "handling_type" && this.decidedDecision !== "approve");
    },
    hasAlerts() {
      return alertsCount(this.claim, this.customer) > 0;
    },
    currentStep() {
      return this.steps[this.currentIndex] || this.steps[0];
    },
    currentDocument() {
      return documentSummary(this.t, this.declaration, this.currentStep);
    },
    isFirstStep() {
      return this.currentIndex === 0;
    },
    // Depuis l'avis de l'agent, c'est la décision qui fait avancer, pas « Suivant ».
    canGoNext() {
      return this.currentIndex < this.steps.length - 1 && !this.pillDisabled[this.currentIndex + 1];
    },
  },

  watch: {
    open: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.currentIndex = 0;
        this.hasDecisionSteps = this.claimApprovalRequired;
        this.decidedDecision = null;
        this.openCount += 1;
      },
    },
  },

  methods: {
    previous() {
      if (!this.isFirstStep) this.currentIndex -= 1;
    },
    next() {
      if (this.canGoNext) this.currentIndex += 1;
    },
    goToStep(type) {
      const index = this.steps.findIndex((step) => step.type === type);
      if (index >= 0) this.currentIndex = index;
    },
    // Une approbation enchaîne sur la prise en charge ; refus et compléments restent sur
    // l'avis, en lecture seule, jusqu'à ce que l'ops ferme.
    onDecided(decision) {
      this.decidedDecision = decision;
      if (decision === "approve") this.goToStep("handling_type");
    },
  },
};
</script>

<i18n src="./AiAnalysisFlowModal.i18n.json"></i18n>
